import { logger } from '@exchange/libs/utils/simple-logger/src';
import { themingInstance } from '@exchange/libs/utils/theming/src';
import type { ChartingLibraryFeatureset } from '@exchange-public/charting_library/charting_library';
import type { ChartPropertiesOverrides, ResolutionString } from '@exchange-public/charting_library/charting_library.d';

import { tradingViewTimeZones } from './model';

export const TV_CHART_CONTAINER = 'tv-chart-container';

function getTradingViewTimeZone(): TradingView.Timezone {
  const offsetInHours = (new Date().getTimezoneOffset() / 60) * -1;
  const tradingViewTimeZone = (Object.keys(tradingViewTimeZones) as Array<TradingView.Timezone>).find((timeZone) => tradingViewTimeZones[timeZone] === offsetInHours);

  if (!tradingViewTimeZone) {
    logger.warn('Cannot find supported Timezone - using UTC');

    return 'Etc/UTC';
  }

  return tradingViewTimeZone;
}

export const getOverrides = (): Partial<ChartPropertiesOverrides> => {
  /** https://github.com/tradingview/charting_library/wiki/Overrides */
  const getColor = themingInstance.getColor.bind(themingInstance);

  const colorPrimary = getColor('--v-theme-primary');
  const colorGreen = getColor('--v-theme-success');
  const colorRed = getColor('--v-theme-error');
  const colorPrimaryWithOpacity = getColor('--primary-lightest');
  const colorBackground2 = getColor('--v-theme-elevation-1');
  const colorBackground5 = getColor('--v-theme-elevation-01');
  const white = 'rgba(255, 255, 255, 1)';

  return {
    'paneProperties.legendProperties.showStudyArguments': true,
    'paneProperties.legendProperties.showStudyTitles': true,
    'paneProperties.legendProperties.showStudyValues': true,
    'paneProperties.legendProperties.showSeriesTitle': true,
    'paneProperties.legendProperties.showSeriesOHLC': true,
    'paneProperties.legendProperties.showLegend': true,

    'paneProperties.backgroundType': 'solid',
    'paneProperties.background': colorBackground2,
    'paneProperties.vertGridProperties.color': colorBackground5,
    'paneProperties.vertGridProperties.style': 0,
    'paneProperties.horzGridProperties.color': colorBackground5,
    'paneProperties.horzGridProperties.style': 0,
    'paneProperties.crossHairProperties.color': getColor('--v-theme-text-secondary'),
    'paneProperties.crossHairProperties.width': 1,
    'paneProperties.crossHairProperties.style': 2,

    'scalesProperties.fontSize': 10,
    'scalesProperties.textColor': getColor('--v-theme-text-primary'),
    'scalesProperties.lineColor': getColor('--v-theme-elevation-3'),
    'scalesProperties.axisHighlightColor': colorPrimaryWithOpacity,
    'scalesProperties.axisLineToolLabelBackgroundColorCommon': colorPrimary,

    'sessions.vertlines.sessBreaks.color': colorPrimary,

    'mainSeriesProperties.baseLineColor': colorPrimary,
    'mainSeriesProperties.showCountdown': true,
    'mainSeriesProperties.showPrevClosePriceLine': true,

    // Candles styles
    'mainSeriesProperties.candleStyle.upColor': colorGreen,
    'mainSeriesProperties.candleStyle.downColor': colorRed,
    'mainSeriesProperties.candleStyle.wickUpColor': colorGreen,
    'mainSeriesProperties.candleStyle.wickDownColor': colorRed,
    'mainSeriesProperties.candleStyle.drawWick': true,
    'mainSeriesProperties.candleStyle.drawBorder': true,
    'mainSeriesProperties.candleStyle.borderColor': colorBackground2,
    'mainSeriesProperties.candleStyle.borderUpColor': colorGreen,
    'mainSeriesProperties.candleStyle.borderDownColor': colorRed,

    // Hollow Candles styles
    'mainSeriesProperties.hollowCandleStyle.upColor': colorGreen,
    'mainSeriesProperties.hollowCandleStyle.downColor': colorRed,
    'mainSeriesProperties.hollowCandleStyle.wickColor': colorGreen,
    'mainSeriesProperties.hollowCandleStyle.wickUpColor': colorGreen,
    'mainSeriesProperties.hollowCandleStyle.wickDownColor': colorRed,
    'mainSeriesProperties.hollowCandleStyle.drawWick': true,
    'mainSeriesProperties.hollowCandleStyle.drawBorder': true,
    'mainSeriesProperties.hollowCandleStyle.borderColor': colorBackground2,
    'mainSeriesProperties.hollowCandleStyle.borderUpColor': colorGreen,
    'mainSeriesProperties.hollowCandleStyle.borderDownColor': colorRed,

    // Heikin Ashi styles
    'mainSeriesProperties.haStyle.upColor': colorGreen,
    'mainSeriesProperties.haStyle.downColor': colorRed,
    'mainSeriesProperties.haStyle.borderColor': colorBackground2,
    'mainSeriesProperties.haStyle.borderUpColor': colorGreen,
    'mainSeriesProperties.haStyle.borderDownColor': colorRed,
    'mainSeriesProperties.haStyle.wickColor': colorGreen,
    'mainSeriesProperties.haStyle.wickUpColor': colorGreen,
    'mainSeriesProperties.haStyle.wickDownColor': colorRed,

    // Bar styles
    'mainSeriesProperties.barStyle.upColor': colorGreen,
    'mainSeriesProperties.barStyle.downColor': colorRed,

    // Line styles
    'mainSeriesProperties.lineStyle.color': colorGreen,

    // Area styles
    'mainSeriesProperties.areaStyle.color1': colorGreen,
    'mainSeriesProperties.areaStyle.color2': colorGreen,
    'mainSeriesProperties.areaStyle.linecolor': colorGreen,

    // Baseline styles
    'mainSeriesProperties.baselineStyle.baselineColor': colorPrimary,
    'mainSeriesProperties.baselineStyle.topFillColor1': colorGreen,
    'mainSeriesProperties.baselineStyle.topFillColor2': colorGreen,
    'mainSeriesProperties.baselineStyle.bottomFillColor1': colorRed,
    'mainSeriesProperties.baselineStyle.bottomFillColor2': colorRed,
    'mainSeriesProperties.baselineStyle.topLineColor': colorGreen,
    'mainSeriesProperties.baselineStyle.bottomLineColor': colorRed,

    // Hi-Lo styles
    'mainSeriesProperties.hiloStyle.color': colorGreen,
    'mainSeriesProperties.hiloStyle.borderColor': colorGreen,
    'mainSeriesProperties.hiloStyle.labelColor': colorGreen,

    // Renko styles
    'mainSeriesProperties.renkoStyle.upColor': colorGreen,
    'mainSeriesProperties.renkoStyle.downColor': colorRed,
    'mainSeriesProperties.renkoStyle.borderUpColor': colorGreen,
    'mainSeriesProperties.renkoStyle.borderDownColor': colorRed,
    'mainSeriesProperties.renkoStyle.upColorProjection': colorGreen,
    'mainSeriesProperties.renkoStyle.downColorProjection': colorRed,
    'mainSeriesProperties.renkoStyle.borderUpColorProjection': colorGreen,
    'mainSeriesProperties.renkoStyle.borderDownColorProjection': colorRed,
    'mainSeriesProperties.renkoStyle.wickUpColor': colorGreen,
    'mainSeriesProperties.renkoStyle.wickDownColor': colorRed,

    // Kagi styles
    'mainSeriesProperties.kagiStyle.upColor': colorGreen,
    'mainSeriesProperties.kagiStyle.downColor': colorRed,
    'mainSeriesProperties.kagiStyle.upColorProjection': colorGreen,
    'mainSeriesProperties.kagiStyle.downColorProjection': colorRed,

    // Point&Figure styles
    'mainSeriesProperties.pnfStyle.upColor': colorGreen,
    'mainSeriesProperties.pnfStyle.downColor': colorRed,
    'mainSeriesProperties.pnfStyle.upColorProjection': colorGreen,
    'mainSeriesProperties.pnfStyle.downColorProjection': colorRed,

    // Range styles
    'mainSeriesProperties.rangeStyle.upColor': colorGreen,
    'mainSeriesProperties.rangeStyle.downColor': colorRed,
    'mainSeriesProperties.rangeStyle.upColorProjection': colorGreen,
    'mainSeriesProperties.rangeStyle.downColorProjection': colorRed,
    'linetoolicon.color': colorPrimary,

    'linetooltrendline.linecolor': white,
    'linetooltrendline.linewidth': 2.0,

    'linetoolinfoline.linecolor': white,
    'linetoolinfoline.linewidth': 2.0,
    'linetoolinfoline.textcolor': colorPrimary,

    'linetoolarrow.linecolor': getColor('--v-theme-yellow'),
    'linetoolarrow.linewidth': 4.0,

    'linetoolbrush.linecolor': white,
    'linetoolbrush.linewidth': 4.0,

    'linetoolextended.linecolor': white,

    'linetoolheadandshoulders.color': white,
    'linetoolheadandshoulders.textcolor': colorPrimary,
    'linetoolheadandshoulders.backgroundColor': getColor('--elevation-light-0'),

    'linetoolriskrewardlong.stopBackground': getColor('--secondary-lightest'),
    'linetoolriskrewardlong.profitBackground': getColor('--primary-lightest'),

    'linetoolriskrewardshort.stopBackground': getColor('--secondary-lightest'),
    'linetoolriskrewardshort.profitBackground': getColor('--primary-lightest'),

    'linetooltext.color': colorPrimary,

    'linetoolfibretracement.horzLabelsAlign': 'right',
    'linetoolfibretracement.transparency': 80,

    'linetoolfibchannel.transparency': 80,

    'linetooldateandpricerange.linecolor': colorPrimary,
  };
};

/**
 *
 * https://github.com/tradingview/charting_library/wiki/Widget-Constructor
 */
export default function getWidgetOptions({
  market,
  interval,
  locale,
  isMobileOnly,
  isNativeApp,
  debug,
  enableGoTo,
  isXSmallScreen,
  theme,
}: {
  market: string;
  interval: ResolutionString;
  locale: TradingView.LanguageCode;
  isMobileOnly: boolean;
  isNativeApp: boolean;
  debug: boolean;
  enableGoTo: boolean;
  isXSmallScreen: boolean;
  theme: TradingView.ThemeName;
}): Omit<TradingView.ChartingLibraryWidgetOptions, 'container' | 'datafeed'> {
  const { getColor, appTheme } = themingInstance;

  const disabledFeatures = [
    /**  https://github.com/tradingview/charting_library/wiki/Featuresets */ ...(!enableGoTo ? ['go_to_date'] : []),
    ...(isMobileOnly || isNativeApp ? ['header_fullscreen_button'] : []),
    ...(isNativeApp ? ['header_screenshot'] : []),
    ...(isXSmallScreen ? ['header_compare'] : []),
    'border_around_the_chart',
    'display_market_status',
    'header_saveload',
    'header_symbol_search',
    'symbol_info',
    'symbol_search_hot_key',
  ] as ChartingLibraryFeatureset[];

  const enabledFeatures = ['charting_library_debug_mode', 'hide_last_na_study_output', 'remove_library_container_border'] as ChartingLibraryFeatureset[];

  const colorBackground10 = getColor('--v-theme-elevation-3');

  return {
    auto_save_delay: 5,
    autosize: true,
    custom_css_url: `../../${process.env.VUE_APP_EXCHANGE_CHART_CSS_NAME}` /** should be an absolute or relative path to the static folder */,
    custom_font_family: appTheme.getCurrentThemeConfig().font['font-family'],
    debug,
    disabled_features: disabledFeatures,
    enabled_features: enabledFeatures,
    fullscreen: false,
    interval,
    library_path: '/charting_library/',
    loading_screen: {
      backgroundColor: getColor('--v-theme-elevation-1'),
      foregroundColor: getColor('--v-theme-elevation-1'),
    },
    locale,
    overrides: getOverrides(),
    studies_overrides: {
      /** https://github.com/tradingview/charting_library/wiki/Studies-Overrides */ 'volume.volume.color.0': colorBackground10,
      'volume.volume.color.1': colorBackground10,
      'volume.show ma': false,
    },
    symbol: market,
    theme,
    timezone: getTradingViewTimeZone(),
    time_frames: [
      { text: '3y', resolution: '1W' as TradingView.ResolutionString },
      { text: '1y', resolution: '1W' as TradingView.ResolutionString },
      { text: '6m', resolution: '240' as TradingView.ResolutionString },
      { text: '3m', resolution: '60' as TradingView.ResolutionString },
      { text: '5d', resolution: '5' as TradingView.ResolutionString },
      { text: '1d', resolution: '1' as TradingView.ResolutionString },
    ],
    toolbar_bg: getColor('--v-theme-elevation-1'),
  };
}
